import AssessmentIcon from '@mui/icons-material/Assessment';
import ArticleIcon from '@mui/icons-material/Article';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BarChartIcon from '@mui/icons-material/BarChart';
import BusinessIcon from '@mui/icons-material/Business';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import CategoryIcon from '@mui/icons-material/Category';
import CodeIcon from '@mui/icons-material/Code';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import DonutSmallIcon from '@mui/icons-material/DonutSmall';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import GridOnIcon from '@mui/icons-material/GridOn';
import GridViewIcon from '@mui/icons-material/GridView';
import GroupsIcon from '@mui/icons-material/Groups';
import InsightsIcon from '@mui/icons-material/Insights';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import PieChartIcon from '@mui/icons-material/PieChart';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import PublicIcon from '@mui/icons-material/Public';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ScatterPlotIcon from '@mui/icons-material/ScatterPlot';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import ViewListIcon from '@mui/icons-material/ViewList';
import WorkIcon from '@mui/icons-material/Work';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import TodayIcon from '@mui/icons-material/Today';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';

import { getDevFlag } from '../constants/devFlags';
import { shouldUseLocalOrFixtureData } from '../utils/dataLoader';
import {
  buildIndustryDatasetNavPath,
  buildIndustryDatasetPath,
  readIndustryDatasetState,
} from './industryDatasetState';
import { ACCESS_POLICY_IDS } from './accessPolicies';

export const ANALYTICS_NAV_GROUP_IDS = {
  DATASETS: 'datasets',
  COVERAGE: 'coverage',
};

export const ANALYTICS_NAV_LEVEL_IDS = {
  MARKET: 'market',
  CATEGORY: 'category',
  PROJECT: 'project',
  PROJECT_DETAIL: 'project-detail',
  ARTIFACT: 'artifact',
  VENDOR: 'vendor',
  COMPANY: 'company',
  VIEW: 'view',
  PORTFOLIO: 'portfolio',
};

export const ANALYTICS_NAV_ITEM_IDS = {
  OPERATING_KPIS: 'operating-kpis',
  JOB_POSTINGS: 'job-postings',
  GUIDANCE: 'guidance',
  FILING_CHANGES: 'filing-changes',
  FEDERAL_DEALS: 'federal-deals',
  PARTNER_TRENDS: 'partner-trends',
  DEVELOPER_SIGNALS: 'developer-signals',
  TECH_STACK: 'tech-stack',
  COVERAGE: 'coverage',
  ACQUISITIONS: 'acquisitions',
  VENTURE_BACKED: 'venture-backed',
  MARKET_BENCHMARKS: 'market-benchmarks',
  EXCHANGE_VOLUME: 'exchange-volume',
  EARNINGS: 'earnings',
};

const view = (definition) => definition;
const level = (definition) => definition;
const item = (definition) => definition;
const DATASET_NAV_ORDER = new Map([
  ANALYTICS_NAV_ITEM_IDS.OPERATING_KPIS,
  ANALYTICS_NAV_ITEM_IDS.JOB_POSTINGS,
  ANALYTICS_NAV_ITEM_IDS.DEVELOPER_SIGNALS,
].map((id, index) => [id, index]));

const adoptionItem = ({ showDailyDeveloper, showDeveloperLeaderboard, showServiceHealth, showArtifactCatalog }) => item({
  id: ANALYTICS_NAV_ITEM_IDS.DEVELOPER_SIGNALS,
  label: 'Adoption Signals',
  description: 'Package and infrastructure adoption signals.',
  icon: CodeIcon,
  accessPolicyId: ACCESS_POLICY_IDS.DEVELOPER_DASHBOARD,
  showAccessBadge: false,
  defaultTo: '/adoption/project/trends',
  levelLinks: {
    [ANALYTICS_NAV_LEVEL_IDS.CATEGORY]: '/adoption/category/trends',
    [ANALYTICS_NAV_LEVEL_IDS.PROJECT]: '/adoption/project/trends',
    [ANALYTICS_NAV_LEVEL_IDS.PROJECT_DETAIL]: '/adoption/project-detail/packages',
  },
  levels: [
    ...(showArtifactCatalog ? [level({ id: 'artifact', label: 'Artifacts', description: 'Package metadata and lifecycle', icon: CodeIcon, sharedState: false, to: '/adoption/artifact/catalog', views: [view({ id: 'artifact-catalog', label: 'Artifact Catalog', icon: ViewListIcon, to: '/adoption/artifact/catalog' })] })] : []),
    level({
      id: ANALYTICS_NAV_LEVEL_IDS.CATEGORY,
      label: 'Market',
      description: 'Market-level adoption benchmarks',
      icon: PublicIcon,
      to: '/adoption/category/trends',
      views: [
        view({ id: 'category-trends', label: 'Dashboard', description: 'Market adoption trends', icon: TimelineIcon, to: '/adoption/category/trends' }),
        ...(showServiceHealth ? [view({ id: 'category-service-health', label: 'Service Health', description: 'Vendor outages and demand-driven incidents', icon: MonitorHeartOutlinedIcon, to: '/adoption/category/service-health' })] : []),
      ],
    }),
    level({
      id: ANALYTICS_NAV_LEVEL_IDS.PROJECT,
      label: 'Project',
      description: 'Project screener and selected trend',
      icon: InsightsIcon,
      to: '/adoption/project/trends',
      views: [
        view({ id: 'project-trends', label: 'Dashboard', description: 'Project trends', icon: TimelineIcon, to: '/adoption/project/trends' }),
        ...(showDailyDeveloper ? [view({ id: 'project-daily', label: 'Daily', description: 'Daily project activity', icon: TodayIcon, to: '/adoption/project/daily', accessPolicyId: ACCESS_POLICY_IDS.DAILY_DEVELOPER_DASHBOARD })] : []),
        ...(showDeveloperLeaderboard ? [view({ id: 'project-leaderboard', label: 'Leaderboard', description: 'Project leaders', icon: LeaderboardIcon, to: '/adoption/project/leaderboard' })] : []),
      ],
    }),
    level({
      id: ANALYTICS_NAV_LEVEL_IDS.PROJECT_DETAIL,
      label: 'Project Detail',
      description: 'Company, project, artifact, and version analysis',
      icon: CodeIcon,
      to: '/adoption/project-detail/packages',
      views: [
        view({ id: 'project-detail-packages', label: 'Project Analysis', description: 'Packages & infrastructure', icon: CodeIcon, to: '/adoption/project-detail/packages' }),
        ...(showDailyDeveloper ? [view({ id: 'project-detail-daily', label: 'Daily', description: 'Daily artifact activity', icon: TodayIcon, to: '/adoption/project-detail/daily', accessPolicyId: ACCESS_POLICY_IDS.DAILY_DEVELOPER_DASHBOARD })] : []),
        view({ id: 'project-detail-versions', label: 'Versions', description: 'Release activity', icon: CategoryIcon, to: '/adoption/project-detail/versions', accessPolicyId: ACCESS_POLICY_IDS.DEVELOPER_VERSIONS_DASHBOARD }),
        ...(showServiceHealth ? [view({ id: 'project-detail-service-health', label: 'Service Health', description: 'Company reported service health', icon: MonitorHeartOutlinedIcon, to: '/adoption/project-detail/service-health' })] : []),
      ],
    }),
  ],
});

const findLevel = (navItem, levelId) => (
  (navItem.levels || []).find((candidate) => candidate.id === levelId)
);

const resolveItemTo = (navItem, activeLevelId, sharedOptions = {}) => {
  const sharedPath = buildIndustryDatasetPath({
    targetDatasetId: navItem.id,
    activeLayerId: activeLevelId,
    filters: sharedOptions.filters,
    projectId: sharedOptions.projectId,
    state: sharedOptions.state,
  });
  if (sharedPath) return sharedPath;

  if (!activeLevelId) return navItem.defaultTo || navItem.to;
  return navItem.levelLinks?.[activeLevelId]
    || navItem.defaultTo
    || navItem.to
    || findLevel(navItem, activeLevelId)?.to;
};

const LAYER_FILTER_RANK = {
  [ANALYTICS_NAV_LEVEL_IDS.MARKET]: 0,
  [ANALYTICS_NAV_LEVEL_IDS.CATEGORY]: 1,
  [ANALYTICS_NAV_LEVEL_IDS.VENDOR]: 2,
  [ANALYTICS_NAV_LEVEL_IDS.PROJECT]: 2,
  [ANALYTICS_NAV_LEVEL_IDS.COMPANY]: 3,
  [ANALYTICS_NAV_LEVEL_IDS.PROJECT_DETAIL]: 3,
};

const shouldCarryFiltersToLayer = (targetLayerId, activeLevelId) => {
  if (!activeLevelId || targetLayerId === activeLevelId) return true;
  const activeRank = LAYER_FILTER_RANK[activeLevelId];
  const targetRank = LAYER_FILTER_RANK[targetLayerId];
  if (activeRank == null || targetRank == null) return false;
  return targetRank >= activeRank;
};

const getLayerScopedSharedOptions = (targetLayerId, activeLevelId, sharedOptions = {}) => {
  const shouldCarryFilters = shouldCarryFiltersToLayer(targetLayerId, activeLevelId);
  return {
    filters: shouldCarryFilters ? sharedOptions.filters : {},
    includeCommonFilters: shouldCarryFilters,
    projectId: sharedOptions.projectId,
    state: sharedOptions.state,
  };
};

const resolveLevelTo = (navItem, navLevel, resolvedTo, activeLevelId, sharedOptions = {}) => {
  // Dataset-local object routes own their URL state outside shared project filters.
  if (navLevel.sharedState === false) return navLevel.to || navLevel.views?.[0]?.to || resolvedTo;
  const scopedOptions = getLayerScopedSharedOptions(navLevel.id, activeLevelId, sharedOptions);
  return buildIndustryDatasetNavPath({
    targetDatasetId: navItem.id,
    layerId: navLevel.id,
    filters: scopedOptions.filters,
    projectId: scopedOptions.projectId,
    includeCommonFilters: scopedOptions.includeCommonFilters,
    state: scopedOptions.state,
  }) || navLevel.to || navLevel.views?.[0]?.to || resolvedTo;
};

const resolveViewTo = (navItem, navLevel, navView, activeLevelId, sharedOptions = {}) => {
  if (navLevel.sharedState === false) return navView.to;
  const scopedOptions = getLayerScopedSharedOptions(navLevel.id, activeLevelId, sharedOptions);
  return buildIndustryDatasetNavPath({
    targetDatasetId: navItem.id,
    layerId: navLevel.id,
    navViewId: navView.id,
    filters: scopedOptions.filters,
    projectId: scopedOptions.projectId,
    includeCommonFilters: scopedOptions.includeCommonFilters,
    state: scopedOptions.state,
  }) || navView.to;
};

const resolveItemViewTo = (navView) => navView.to;

const withResolvedItemLinks = (navItem, activeLevelId, sharedOptions = {}) => {
  const resolvedTo = resolveItemTo(navItem, activeLevelId, sharedOptions);

  return {
    ...navItem,
    to: resolvedTo,
    views: (navItem.views || []).map((navView) => ({
      ...navView,
      to: resolveItemViewTo(navView),
    })),
    levels: (navItem.levels || []).map((navLevel) => ({
      ...navLevel,
      to: resolveLevelTo(navItem, navLevel, resolvedTo, activeLevelId, sharedOptions),
      views: (navLevel.views || []).map((navView) => ({
        ...navView,
        to: resolveViewTo(navItem, navLevel, navView, activeLevelId, sharedOptions),
      })),
    })),
  };
};

const buildDatasetItems = () => {
  const showSIHiring = getDevFlag('SI_HIRING');
  const showGuidance = getDevFlag('GUIDANCE_PREVIEW');
  const showTechStack = getDevFlag('TECH_STACK');
  const showFilingChanges = getDevFlag('FILING_INTELLIGENCE_PREVIEW');
  const showDailyHiring = getDevFlag('JOB_POSTINGS_DAILY');
  const showDailyDeveloper = getDevFlag('DEVELOPER_SIGNALS_DAILY');
  const showDeveloperLeaderboard = getDevFlag('DEVELOPER_SIGNALS_LEADERBOARD');
  const showFederalDeals = getDevFlag('FEDERAL_DEALS');
  const showServiceHealth = getDevFlag('SERVICE_HEALTH');
  const showArtifactCatalog = getDevFlag('ADOPTION_ARTIFACT_CATALOG') && shouldUseLocalOrFixtureData();

  return [
    item({
      id: ANALYTICS_NAV_ITEM_IDS.OPERATING_KPIS,
      label: 'Operating KPIs',
      description: 'Operating performance, valuation, and management guidance by market, vendor, and company.',
      icon: AssessmentIcon,
      defaultTo: '/kpis/vendor/table',
      levelLinks: {
        [ANALYTICS_NAV_LEVEL_IDS.CATEGORY]: '/kpis/category/trends',
        [ANALYTICS_NAV_LEVEL_IDS.VENDOR]: '/kpis/vendor/table',
        [ANALYTICS_NAV_LEVEL_IDS.COMPANY]: '/kpis/company/financial-overview',
      },
      levels: [
        level({
          id: ANALYTICS_NAV_LEVEL_IDS.CATEGORY,
          label: 'Market',
          description: 'Aggregate KPI trends across market cohorts',
          icon: PublicIcon,
          to: '/kpis/category/trends',
          views: [
            view({ id: 'category-trends', label: 'KPI Trends', description: 'KPI trends and medians', icon: BarChartIcon, to: '/kpis/category/trends' }),
            view({ id: 'category-valuation', label: 'Valuation Benchmarks', description: 'Valuation ranges', icon: ScatterPlotIcon, to: '/kpis/category/valuation' }),
            view({ id: 'category-trended', label: 'KPI Distribution', description: 'Sector metric ranges', icon: CandlestickChartIcon, to: '/kpis/category/trended' }),
            ...(showGuidance ? [
              view({ id: 'category-guidance-results', label: 'Guidance', description: 'Guidance by market', icon: FactCheckIcon, to: '/kpis/category/guidance-results', accessPolicyId: ACCESS_POLICY_IDS.GUIDANCE_DASHBOARD }),
            ] : []),
          ],
        }),
        level({
          id: ANALYTICS_NAV_LEVEL_IDS.VENDOR,
          label: 'Vendor',
          description: 'Company-by-company KPI benchmarks',
          icon: InsightsIcon,
          to: '/kpis/vendor/table',
          views: [
            view({ id: 'vendor-table', label: 'Comp Sheet', description: 'Company fundamentals', icon: GridViewIcon, to: '/kpis/vendor/table' }),
            view({ id: 'vendor-chart', label: 'Current Metrics', description: 'Performance and value', icon: BarChartIcon, to: '/kpis/vendor/chart' }),
            ...(showGuidance ? [
              view({ id: 'vendor-guidance-results', label: 'Guidance', description: 'Guidance and revisions', icon: FactCheckIcon, to: '/kpis/vendor/guidance-results', accessPolicyId: ACCESS_POLICY_IDS.GUIDANCE_DASHBOARD }),
            ] : []),
          ],
        }),
        level({
          id: ANALYTICS_NAV_LEVEL_IDS.COMPANY,
          label: 'Company Detail',
          description: 'Single-company financial detail',
          icon: BusinessIcon,
          to: '/kpis/company/financial-overview',
          views: [
            view({ id: 'company-financial-overview', label: 'Financial Overview', description: 'Financial performance', icon: AccountBalanceIcon, to: '/kpis/company/financial-overview' }),
            ...(showGuidance ? [
              view({ id: 'company-guidance-results', label: 'Guidance', description: 'Guidance and revisions', icon: FactCheckIcon, to: '/kpis/company/guidance-results', accessPolicyId: ACCESS_POLICY_IDS.GUIDANCE_DASHBOARD }),
            ] : []),
          ],
        }),
      ],
    }),
    adoptionItem({ showDailyDeveloper, showDeveloperLeaderboard, showServiceHealth, showArtifactCatalog }),
    item({
      id: ANALYTICS_NAV_ITEM_IDS.JOB_POSTINGS,
      label: 'Job Postings',
      description: 'Company hiring demand, role mix, active openings, and partner ecosystem trends.',
      icon: WorkOutlineIcon,
      accessPolicyId: ACCESS_POLICY_IDS.HIRING_DASHBOARD,
      showAccessBadge: false,
      defaultTo: '/hiring/vendor/trend',
      levelLinks: {
        [ANALYTICS_NAV_LEVEL_IDS.CATEGORY]: '/hiring/category/trend',
        [ANALYTICS_NAV_LEVEL_IDS.VENDOR]: '/hiring/vendor/trend',
        [ANALYTICS_NAV_LEVEL_IDS.COMPANY]: '/hiring/company/trend',
      },
      levels: [
        level({
          id: ANALYTICS_NAV_LEVEL_IDS.CATEGORY,
          label: 'Market',
          description: 'Market-wide hiring signals and segmented trends',
          icon: PublicIcon,
          to: '/hiring/category/trend',
          views: [
            view({ id: 'category-trend', label: 'Hiring Trends', description: 'Postings by market', icon: TimelineIcon, to: '/hiring/category/trend' }),
            view({ id: 'category-leadership', label: 'Leadership Hiring', description: 'Executive job postings', icon: WorkIcon, to: '/hiring/category/leadership' }),
            ...(showSIHiring ? [view({ id: 'category-partner-trends', label: 'Partner Trends', description: 'Partner postings', icon: TimelineIcon, to: '/hiring/category/partner-trends', accessPolicyId: ACCESS_POLICY_IDS.PARTNER_TRENDS_DASHBOARD })] : []),
          ],
        }),
        level({
          id: ANALYTICS_NAV_LEVEL_IDS.VENDOR,
          label: 'Vendor',
          description: 'Company-by-company hiring signals',
          icon: InsightsIcon,
          to: '/hiring/vendor/trend',
          views: [
            view({ id: 'vendor-trend', label: 'Company Hiring Trends', description: 'Hiring across companies', icon: TimelineIcon, to: '/hiring/vendor/trend' }),
            view({ id: 'vendor-roles', label: 'Role Mix', description: 'Roles across postings', icon: PieChartIcon, to: '/hiring/vendor/roles' }),
            ...(showDailyHiring ? [view({ id: 'vendor-daily', label: 'Daily Hiring', description: 'Daily hiring activity', icon: TodayIcon, to: '/hiring/vendor/daily', accessPolicyId: ACCESS_POLICY_IDS.DAILY_HIRING_DASHBOARD })] : []),
            ...(showSIHiring ? [view({ id: 'vendor-partner-trends', label: 'Partner Trends', description: 'Partner postings', icon: TimelineIcon, to: '/hiring/vendor/partner-trends', accessPolicyId: ACCESS_POLICY_IDS.PARTNER_TRENDS_DASHBOARD })] : []),
          ],
        }),
        level({
          id: ANALYTICS_NAV_LEVEL_IDS.COMPANY,
          label: 'Company Detail',
          description: 'Single-company hiring detail',
          icon: BusinessIcon,
          to: '/hiring/company/trend',
          views: [
            view({ id: 'company-trend', label: 'Posting Trend', description: 'Weekly posting activity', icon: TimelineIcon, to: '/hiring/company/trend' }),
            view({ id: 'company-jobBoard', label: 'Active Job Board', description: 'Active openings', icon: ViewListIcon, to: '/hiring/company/jobBoard' }),
            ...(showDailyHiring ? [view({ id: 'company-daily', label: 'Daily Hiring', description: 'Daily hiring activity', icon: TodayIcon, to: '/hiring/company/daily', accessPolicyId: ACCESS_POLICY_IDS.DAILY_HIRING_DASHBOARD })] : []),
            ...(showSIHiring ? [
              view({ id: 'company-partner-trends', label: 'Partner Trends', description: 'Partner postings', icon: TimelineIcon, to: '/hiring/company/partner-trends', accessPolicyId: ACCESS_POLICY_IDS.PARTNER_TRENDS_DASHBOARD }),
            ] : []),
          ],
        }),
      ],
    }),
    false && item({
      id: ANALYTICS_NAV_ITEM_IDS.GUIDANCE,
      label: 'Guidance',
      description: 'Management guidance results, revisions, and company detail.',
      icon: FactCheckIcon,
      badge: 'Preview',
      defaultTo: '/guidance/vendor/results',
      levelLinks: {
        [ANALYTICS_NAV_LEVEL_IDS.CATEGORY]: '/guidance/category/results',
        [ANALYTICS_NAV_LEVEL_IDS.VENDOR]: '/guidance/vendor/results',
        [ANALYTICS_NAV_LEVEL_IDS.COMPANY]: '/guidance/company/results',
      },
      levels: [
        level({
          id: ANALYTICS_NAV_LEVEL_IDS.CATEGORY,
          label: 'Market',
          description: 'Aggregate guidance behavior across software markets',
          icon: PublicIcon,
          to: '/guidance/category/results',
          views: [
            view({ id: 'category-results', label: 'Results', description: 'Actuals vs guidance by category', icon: AssessmentIcon, to: '/guidance/category/results' }),
            view({ id: 'category-revisions', label: 'Revisions', description: 'FY guide revisions by category', icon: TimelineIcon, to: '/guidance/category/revisions' }),
          ],
        }),
        level({
          id: ANALYTICS_NAV_LEVEL_IDS.VENDOR,
          label: 'Vendor',
          description: 'Company guidance screens',
          icon: InsightsIcon,
          to: '/guidance/vendor/results',
          views: [
            view({ id: 'vendor-results', label: 'Results', description: 'Company result beats and misses', icon: AssessmentIcon, to: '/guidance/vendor/results' }),
            view({ id: 'vendor-revisions', label: 'Revisions', description: 'Company FY guide revisions', icon: TimelineIcon, to: '/guidance/vendor/revisions' }),
          ],
        }),
        level({
          id: ANALYTICS_NAV_LEVEL_IDS.COMPANY,
          label: 'Company Detail',
          description: 'Single-company guidance detail',
          icon: BusinessIcon,
          to: '/guidance/company/results',
          views: [
            view({ id: 'company-results', label: 'Results', description: 'Detailed results vs guidance', icon: AssessmentIcon, to: '/guidance/company/results' }),
            view({ id: 'company-revisions', label: 'Revisions', description: 'Detailed FY guide revisions', icon: TimelineIcon, to: '/guidance/company/revisions' }),
          ],
        }),
      ],
    }),
    showFilingChanges && item({
      id: ANALYTICS_NAV_ITEM_IDS.FILING_CHANGES,
      label: 'Filing Analytics',
      description: 'Analyze flagged disclosure changes by vendor and company.',
      icon: FindInPageIcon,
      badge: 'Preview',
      defaultTo: '/filingchanges/vendor/events',
      levelLinks: {
        [ANALYTICS_NAV_LEVEL_IDS.VENDOR]: '/filingchanges/vendor/events',
        [ANALYTICS_NAV_LEVEL_IDS.COMPANY]: '/filingchanges/company/events',
      },
      levels: [
        level({
          id: ANALYTICS_NAV_LEVEL_IDS.VENDOR,
          label: 'Vendor',
          description: 'Company-by-company filing changes',
          icon: InsightsIcon,
          to: '/filingchanges/vendor/events',
          views: [
            view({ id: 'vendor-events', label: 'Events', description: 'Company filing event table', icon: GridViewIcon, to: '/filingchanges/vendor/events' }),
          ],
        }),
        level({
          id: ANALYTICS_NAV_LEVEL_IDS.COMPANY,
          label: 'Company Detail',
          description: 'Single-company filing changes',
          icon: BusinessIcon,
          to: '/filingchanges/company/events',
          views: [
            view({ id: 'company-events', label: 'Events', description: 'Company filing change detail', icon: FindInPageIcon, to: '/filingchanges/company/events' }),
            view({ id: 'company-definitions', label: 'Metric Definitions', description: 'Latest KPI definitions and change review', icon: ArticleIcon, to: '/filingchanges/company/definitions' }),
          ],
        }),
      ],
    }),
    showFederalDeals && item({
      id: ANALYTICS_NAV_ITEM_IDS.FEDERAL_DEALS,
      label: 'Federal Deals',
      description: 'Analyze federal software purchases, products, pricing, and lifecycle signals.',
      icon: AccountBalanceIcon,
      defaultTo: '/federal-deals/portfolio/deals',
      levelLinks: {
        [ANALYTICS_NAV_LEVEL_IDS.PORTFOLIO]: '/federal-deals/portfolio/deals',
        [ANALYTICS_NAV_LEVEL_IDS.COMPANY]: '/federal-deals/company/relationships',
      },
      levels: [
        level({
          id: ANALYTICS_NAV_LEVEL_IDS.PORTFOLIO,
          label: 'Portfolio',
          description: 'Cross-vendor federal activity',
          icon: InsightsIcon,
          to: '/federal-deals/portfolio/deals',
          views: [
            view({ id: 'portfolio-overview', label: 'Overview', description: 'Quarterly funding and annualized obligations', icon: TimelineIcon, to: '/federal-deals/portfolio/overview' }),
            view({ id: 'portfolio-spend', label: 'Buyer & Product', description: 'Quarterly vendor, buyer and product pivots', icon: InsightsIcon, to: '/federal-deals/portfolio/spend' }),
            view({ id: 'portfolio-expirations', label: 'Expirations', description: 'Largest contracts reaching current end dates', icon: TimelineIcon, to: '/federal-deals/portfolio/expirations' }),
            view({ id: 'portfolio-deals', label: 'Deals', description: 'All classified award groups', icon: GridViewIcon, to: '/federal-deals/portfolio/deals' }),
          ],
        }),
        level({
          id: ANALYTICS_NAV_LEVEL_IDS.COMPANY,
          label: 'Company',
          description: 'Vendor relationships by federal organization',
          icon: BusinessIcon,
          to: '/federal-deals/company/relationships',
          views: [
            view({ id: 'company-relationships', label: 'Relationship Analytics', description: 'Quarterly contract values, buyer relationships and expirations', icon: BusinessIcon, to: '/federal-deals/company/relationships' }),
          ],
        }),
      ],
    }),
    false && item({
      id: ANALYTICS_NAV_ITEM_IDS.PARTNER_TRENDS,
      label: 'Partner Trends',
      description: 'System integrator hiring tied to software vendor ecosystems.',
      icon: WorkIcon,
      badge: 'Preview',
      defaultTo: '/sihiring/vendor/trends',
      levelLinks: {
        [ANALYTICS_NAV_LEVEL_IDS.MARKET]: '/sihiring/market/trends',
        [ANALYTICS_NAV_LEVEL_IDS.CATEGORY]: '/sihiring/category/trends',
        [ANALYTICS_NAV_LEVEL_IDS.VENDOR]: '/sihiring/vendor/trends',
        [ANALYTICS_NAV_LEVEL_IDS.COMPANY]: '/sihiring/company/dashboard',
      },
      levels: [
        level({
          id: ANALYTICS_NAV_LEVEL_IDS.MARKET,
          label: 'Market',
          description: 'Total SI market trend',
          icon: PublicIcon,
          to: '/sihiring/market/trends',
          views: [
            view({ id: 'market-trends', label: 'Partner Trends', description: 'Total SI demand', icon: TimelineIcon, to: '/sihiring/market/trends' }),
          ],
        }),
        level({
          id: ANALYTICS_NAV_LEVEL_IDS.CATEGORY,
          label: 'Category',
          description: 'Same-store category trends',
          icon: CategoryIcon,
          to: '/sihiring/category/trends',
          views: [
            view({ id: 'category-trends', label: 'Partner Trends', description: 'Category partner demand', icon: TimelineIcon, to: '/sihiring/category/trends' }),
          ],
        }),
        level({
          id: ANALYTICS_NAV_LEVEL_IDS.VENDOR,
          label: 'Vendor',
          description: 'Vendor-tagged SI demand',
          icon: InsightsIcon,
          to: '/sihiring/vendor/trends',
          views: [
            view({ id: 'vendor-trends', label: 'Partner Trends', description: 'Vendor partner hiring trends', icon: TimelineIcon, to: '/sihiring/vendor/trends' }),
          ],
        }),
        level({
          id: ANALYTICS_NAV_LEVEL_IDS.COMPANY,
          label: 'Company Detail',
          description: 'Single-vendor partner demand',
          icon: BusinessIcon,
          to: '/sihiring/company/dashboard',
          views: [
            view({ id: 'company-dashboard', label: 'Partner Trends', description: 'Partner hiring trend', icon: TimelineIcon, to: '/sihiring/company/dashboard' }),
          ],
        }),
      ],
    }),
    showTechStack && item({
      id: ANALYTICS_NAV_ITEM_IDS.TECH_STACK,
      label: 'Tech Stack',
      description: 'Technology adoption and stack composition.',
      icon: GridOnIcon,
      badge: 'Preview',
      defaultTo: '/techstack',
      levels: [],
    }),
    item({
      id: ANALYTICS_NAV_ITEM_IDS.MARKET_BENCHMARKS,
      label: 'Market Benchmarks',
      description: 'Index performance, valuation, and market structure.',
      icon: ShowChartIcon,
      defaultTo: '/benchmarks/index',
      levels: [],
      views: [
        view({ id: 'index', label: 'Index Performance', description: 'Software sector returns', icon: ShowChartIcon, to: '/benchmarks/index' }),
        view({ id: 'valuation', label: 'Market Valuation', description: 'Valuation trends', icon: PriceCheckIcon, to: '/benchmarks/valuation' }),
        view({ id: 'treemap', label: 'Market Map', description: 'Market structure', icon: DonutSmallIcon, to: '/benchmarks/treemap' }),
      ],
    }),
  ].filter(Boolean).sort((left, right) => (
    (DATASET_NAV_ORDER.get(left.id) ?? Number.MAX_SAFE_INTEGER)
    - (DATASET_NAV_ORDER.get(right.id) ?? Number.MAX_SAFE_INTEGER)
  ));
};

const buildCoverageItems = () => {
  const showExchangeVolume = getDevFlag('EXCHANGE_VOLUME');
  const showFullAcquisitions = getDevFlag('ACQUISITIONS_FULL');
  const showAiTracking = getDevFlag('AI_TRACKING');

  return [
    item({
      id: ANALYTICS_NAV_ITEM_IDS.COVERAGE,
      label: 'Coverage',
      description: 'Tracked companies and categories.',
      icon: PublicIcon,
      defaultTo: '/marketinsights/coverage/coverage/table',
      levels: [],
      views: [
        view({ id: 'table', label: 'Coverage Table', description: 'All tracked companies', icon: PublicIcon, to: '/marketinsights/coverage/coverage/table' }),
      ],
    }),
    item({
      id: ANALYTICS_NAV_ITEM_IDS.ACQUISITIONS,
      label: 'Acquisitions',
      description: 'Software M&A comps and transaction analytics.',
      icon: BusinessIcon,
      defaultTo: '/marketinsights/coverage/acquisitions/public',
      levels: [],
      views: [
        ...(showFullAcquisitions ? [
          view({ id: 'market', label: 'Market', description: 'Total M&A volume and mix', icon: LeaderboardIcon, to: '/marketinsights/coverage/acquisitions/market' }),
          view({ id: 'category', label: 'Category', description: 'Category acquisition trends', icon: CategoryIcon, to: '/marketinsights/coverage/acquisitions/category' }),
          view({ id: 'list', label: 'List', description: 'Total and public transaction lists', icon: GridOnIcon, to: '/marketinsights/coverage/acquisitions/list' }),
        ] : []),
        view({ id: 'public', label: 'Public Acquisitions', description: 'Public software takeouts', icon: BusinessIcon, to: '/marketinsights/coverage/acquisitions/public' }),
      ],
    }),
    item({
      id: ANALYTICS_NAV_ITEM_IDS.VENTURE_BACKED,
      label: 'Venture',
      description: 'Venture-backed software funding and valuation coverage.',
      icon: RocketLaunchIcon,
      defaultTo: '/marketinsights/coverage/private-funding/venture-private',
      levels: [],
      views: [
        view({ id: 'venture-private', label: 'Venture', description: 'Funding and valuation coverage', icon: RocketLaunchIcon, to: '/marketinsights/coverage/private-funding/venture-private' }),
        ...(showAiTracking ? [
          view({ id: 'ai-pulse', label: 'AI Pulse', description: 'What changed across AI-native signals', icon: AutoAwesomeIcon, badge: 'Core', to: '/marketinsights/coverage/private-funding/ai-pulse' }),
          view({ id: 'ai-hiring', label: 'Hiring & GTM', description: 'Hiring velocity and role-mix shifts', icon: GroupsIcon, badge: 'Core', to: '/marketinsights/coverage/private-funding/ai-hiring' }),
          view({ id: 'ai-developer', label: 'Developer Adoption', description: 'Project usage and comparable-product share', icon: CodeIcon, badge: 'Core', to: '/marketinsights/coverage/private-funding/ai-developer' }),
          view({ id: 'ai-capital', label: 'Capital & ARR', description: 'ARR, funding, and valuation history', icon: AccountBalanceIcon, badge: 'Core', to: '/marketinsights/coverage/private-funding/ai-capital' }),
        ] : []),
      ],
    }),
    showExchangeVolume && item({
      id: ANALYTICS_NAV_ITEM_IDS.EXCHANGE_VOLUME,
      label: 'Exchange Volume',
      description: 'Options and futures volume data.',
      icon: CurrencyExchangeIcon,
      badge: 'Preview',
      defaultTo: '/marketinsights/exchangevolume',
      levels: [],
      views: [
        view({ id: 'trends', label: 'Dashboard', description: 'Track volume over time', icon: TimelineIcon, to: '/marketinsights/exchangevolume' }),
        view({ id: 'table', label: 'Data Table', description: 'Detailed volume data', icon: GridOnIcon, to: '/marketinsights/exchangevolume' }),
        view({ id: 'marketShare', label: 'Market Share', description: 'Exchange breakdown', icon: DonutSmallIcon, to: '/marketinsights/exchangevolume' }),
      ],
    }),
  ].filter(Boolean);
};

const buildBaseGroups = () => [
  {
    id: ANALYTICS_NAV_GROUP_IDS.DATASETS,
    label: 'Industry Datasets',
    items: buildDatasetItems(),
  },
  {
    id: ANALYTICS_NAV_GROUP_IDS.COVERAGE,
    label: 'Market Universe',
    items: buildCoverageItems(),
  },
];

const getActiveGroupId = (activeItemId) => {
  const groups = buildBaseGroups();
  return groups.find((group) => group.items.some((candidate) => candidate.id === activeItemId))?.id
    || ANALYTICS_NAV_GROUP_IDS.DATASETS;
};

export const buildAnalyticsSidebarGroups = ({
  activeItemId,
  activeGroupId,
  activeLevelId,
  filters,
  projectId,
  state,
} = {}) => {
  const visibleGroupId = activeGroupId || getActiveGroupId(activeItemId);
  const sharedOptions = {
    filters,
    projectId,
    state: state || readIndustryDatasetState(),
  };

  return buildBaseGroups()
    .filter((group) => group.id === visibleGroupId)
    .map((group) => ({
      ...group,
      items: group.items.map((navItem) => (
        group.id === ANALYTICS_NAV_GROUP_IDS.DATASETS
          ? withResolvedItemLinks(navItem, activeLevelId, sharedOptions)
          : withResolvedItemLinks(navItem, activeLevelId)
      )),
    }));
};

export const getAnalyticsSidebarStateFromPath = (pathname = '') => {
  const parts = pathname.split('/').filter(Boolean);

  if (parts[0] === 'adoption') {
    const levelId = parts[1] || ANALYTICS_NAV_LEVEL_IDS.PROJECT;
    const viewId = parts[2] || (levelId === ANALYTICS_NAV_LEVEL_IDS.PROJECT_DETAIL ? 'packages' : 'trends');
    return {
      activeNavigationGroupId: ANALYTICS_NAV_GROUP_IDS.DATASETS,
      activeNavigationItemId: ANALYTICS_NAV_ITEM_IDS.DEVELOPER_SIGNALS,
      activeNavigationLevelId: levelId,
      activeNavigationViewId: `${levelId}-${viewId}`,
    };
  }

  if (parts[0] === 'kpis') {
    const levelId = parts[1] || ANALYTICS_NAV_LEVEL_IDS.VENDOR;
    const viewId = parts[2] || (levelId === ANALYTICS_NAV_LEVEL_IDS.CATEGORY ? 'trends' : 'table');

    return {
      activeNavigationGroupId: ANALYTICS_NAV_GROUP_IDS.DATASETS,
      activeNavigationItemId: ANALYTICS_NAV_ITEM_IDS.OPERATING_KPIS,
      activeNavigationLevelId: levelId,
      activeNavigationViewId: `${levelId}-${viewId}`,
    };
  }

  if (parts[0] === 'hiring') {
    const levelId = parts[1] || ANALYTICS_NAV_LEVEL_IDS.VENDOR;
    return {
      activeNavigationGroupId: ANALYTICS_NAV_GROUP_IDS.DATASETS,
      activeNavigationItemId: ANALYTICS_NAV_ITEM_IDS.JOB_POSTINGS,
      activeNavigationLevelId: levelId,
      activeNavigationViewId: `${levelId}-${parts[2] || 'trend'}`,
    };
  }

  if (parts[0] === 'guidance') {
    const levelId = parts[1] || ANALYTICS_NAV_LEVEL_IDS.VENDOR;
    const viewId = parts[2] || 'results';
    return {
      activeNavigationGroupId: ANALYTICS_NAV_GROUP_IDS.DATASETS,
      activeNavigationItemId: ANALYTICS_NAV_ITEM_IDS.OPERATING_KPIS,
      activeNavigationLevelId: levelId,
      activeNavigationViewId: `${levelId}-guidance-${viewId}`,
    };
  }

  if (parts[0] === 'filingchanges') {
    const levelId = parts[1] === ANALYTICS_NAV_LEVEL_IDS.CATEGORY
      ? ANALYTICS_NAV_LEVEL_IDS.VENDOR
      : parts[1] || ANALYTICS_NAV_LEVEL_IDS.VENDOR;
    const viewId = parts[2] || 'events';
    return {
      activeNavigationGroupId: ANALYTICS_NAV_GROUP_IDS.DATASETS,
      activeNavigationItemId: ANALYTICS_NAV_ITEM_IDS.FILING_CHANGES,
      activeNavigationLevelId: levelId,
      activeNavigationViewId: `${levelId}-${viewId}`,
    };
  }

  if (parts[0] === 'federal-deals') {
    const levelId = parts[1] || ANALYTICS_NAV_LEVEL_IDS.PORTFOLIO;
    const viewId = parts[2] || 'deals';
    return {
      activeNavigationGroupId: ANALYTICS_NAV_GROUP_IDS.DATASETS,
      activeNavigationItemId: ANALYTICS_NAV_ITEM_IDS.FEDERAL_DEALS,
      activeNavigationLevelId: levelId,
      activeNavigationViewId: `${levelId}-${viewId}`,
    };
  }

  if (parts[0] === 'sihiring') {
    const levelId = parts[1] || ANALYTICS_NAV_LEVEL_IDS.VENDOR;
    const viewId = parts[2] || (levelId === ANALYTICS_NAV_LEVEL_IDS.COMPANY ? 'dashboard' : 'trends');

    return {
      activeNavigationGroupId: ANALYTICS_NAV_GROUP_IDS.DATASETS,
      activeNavigationItemId: ANALYTICS_NAV_ITEM_IDS.JOB_POSTINGS,
      activeNavigationLevelId: levelId,
      activeNavigationViewId: `${levelId}-partner-${viewId === 'postings' ? 'jobs' : 'trends'}`,
    };
  }

  if (parts[0] === 'developer') {
    if (parts[1] === ANALYTICS_NAV_LEVEL_IDS.COMPANY) {
      return {
        activeNavigationGroupId: ANALYTICS_NAV_GROUP_IDS.DATASETS,
        activeNavigationItemId: null,
        activeNavigationLevelId: null,
        activeNavigationViewId: null,
      };
    }
    const levelId = parts[1] || ANALYTICS_NAV_LEVEL_IDS.PROJECT;
    const viewId = parts[2] || 'trends';

    return {
      activeNavigationGroupId: ANALYTICS_NAV_GROUP_IDS.DATASETS,
      activeNavigationItemId: ANALYTICS_NAV_ITEM_IDS.DEVELOPER_SIGNALS,
      activeNavigationLevelId: levelId,
      activeNavigationViewId: `${levelId}-${viewId}`,
    };
  }

  if (parts[0] === 'marketinsights' && parts[1] === 'coverage') {
    const section = parts[2] || 'coverage';
    const viewId = parts[3];

    if (section === 'acquisitions') {
      return {
        activeNavigationGroupId: ANALYTICS_NAV_GROUP_IDS.COVERAGE,
        activeNavigationItemId: ANALYTICS_NAV_ITEM_IDS.ACQUISITIONS,
        activeNavigationLevelId: ANALYTICS_NAV_LEVEL_IDS.VIEW,
        activeNavigationViewId: viewId || 'public',
      };
    }

    if (section === 'private-funding') {
      return {
        activeNavigationGroupId: ANALYTICS_NAV_GROUP_IDS.COVERAGE,
        activeNavigationItemId: ANALYTICS_NAV_ITEM_IDS.VENTURE_BACKED,
        activeNavigationLevelId: ANALYTICS_NAV_LEVEL_IDS.VIEW,
        activeNavigationViewId: viewId === 'growth-equity' ? 'venture-private' : viewId || 'venture-private',
      };
    }

    return {
      activeNavigationGroupId: ANALYTICS_NAV_GROUP_IDS.COVERAGE,
      activeNavigationItemId: ANALYTICS_NAV_ITEM_IDS.COVERAGE,
      activeNavigationLevelId: ANALYTICS_NAV_LEVEL_IDS.VIEW,
      activeNavigationViewId: viewId || 'table',
    };
  }

  if (pathname === '/marketinsights/acquisitions') {
    return {
      activeNavigationGroupId: ANALYTICS_NAV_GROUP_IDS.COVERAGE,
      activeNavigationItemId: ANALYTICS_NAV_ITEM_IDS.ACQUISITIONS,
      activeNavigationLevelId: ANALYTICS_NAV_LEVEL_IDS.VIEW,
      activeNavigationViewId: 'public',
    };
  }

  if (pathname === '/marketinsights/venture-backed') {
    return {
      activeNavigationGroupId: ANALYTICS_NAV_GROUP_IDS.COVERAGE,
      activeNavigationItemId: ANALYTICS_NAV_ITEM_IDS.VENTURE_BACKED,
      activeNavigationLevelId: ANALYTICS_NAV_LEVEL_IDS.VIEW,
      activeNavigationViewId: 'venture-private',
    };
  }

  if (parts[0] === 'benchmarks') {
    return {
      activeNavigationGroupId: ANALYTICS_NAV_GROUP_IDS.DATASETS,
      activeNavigationItemId: ANALYTICS_NAV_ITEM_IDS.MARKET_BENCHMARKS,
      activeNavigationLevelId: ANALYTICS_NAV_LEVEL_IDS.VIEW,
      activeNavigationViewId: parts[1] || 'index',
    };
  }

  if (parts[0] === 'marketinsights' && parts[1] === 'exchangevolume') {
    return {
      activeNavigationGroupId: ANALYTICS_NAV_GROUP_IDS.COVERAGE,
      activeNavigationItemId: ANALYTICS_NAV_ITEM_IDS.EXCHANGE_VOLUME,
      activeNavigationLevelId: ANALYTICS_NAV_LEVEL_IDS.VIEW,
      activeNavigationViewId: parts[2] || 'trends',
    };
  }

  if (parts[0] === 'marketinsights' && parts[1] === 'earnings') {
    return {
      activeNavigationGroupId: ANALYTICS_NAV_GROUP_IDS.COVERAGE,
      activeNavigationItemId: ANALYTICS_NAV_ITEM_IDS.EARNINGS,
      activeNavigationLevelId: ANALYTICS_NAV_LEVEL_IDS.VIEW,
      activeNavigationViewId: parts[2] || 'calendar',
    };
  }

  return {
    activeNavigationGroupId: ANALYTICS_NAV_GROUP_IDS.DATASETS,
    activeNavigationItemId: null,
    activeNavigationLevelId: null,
    activeNavigationViewId: null,
  };
};

export const buildAnalyticsSidebarProps = ({ pathname } = {}) => {
  const state = getAnalyticsSidebarStateFromPath(pathname);

  return {
    ...state,
    sidebarGroups: buildAnalyticsSidebarGroups({
      activeGroupId: state.activeNavigationGroupId,
      activeItemId: state.activeNavigationItemId,
      activeLevelId: state.activeNavigationLevelId,
    }),
  };
};
